<template>
  <div class="form-group">
    <div class="grey mb5">{{ label ?? name }}</div>

    <div class="d-flex align-items-center">
      <label class="media-upload w-100" for="media-upload">
        <span>{{ displayText }}</span>
        <div class="icon">
          <img :src="fieldIcon" alt="upload" style="width: 18px; height: 18px;">
        </div>
      </label>
    </div>

    <!-- File List -->
    <div v-if="files.length > 0" class="file-list mt-2">
      <div v-for="(file, index) in files" :key="index"
        class="file-item d-flex align-items-center justify-content-between p-2 mb-2 border rounded">
        <div class="file-info d-flex align-items-center flex-1">
          <div class="file-type-icon me-2">
            <span v-if="file.type === 'image'" class="icon-image">🖼️</span>
            <span v-else-if="file.type === 'pdf'" class="icon-pdf">📄</span>
            <span v-else class="icon-file">📎</span>
          </div>
          <div class="file-details">
            <div class="file-name font-weight-bold">{{ truncate(file.name, 30) }}</div>
            <!-- <div class="file-size text-muted ft-12">{{ file.size }}</div> -->
          </div>
        </div>
        <div class="file-actions d-flex align-items-center">
          <button v-if="file.previewUrl" type="button" @click="openPreview(file)" class="btn btn-see ft-14 px-2 me-2">
            <small>Preview</small>
          </button>
          <button type="button" @click="removeFile(index)" class="btn btn-danger text-white ft-14 px-2">
            <small>Remove</small>
          </button>
        </div>
      </div>
    </div>

    <!-- Hidden input for validation -->
    <input ref="validationInput" type="hidden" :name="name" :value="validationValue" v-validate="rules"
      :data-vv-as="label || name">

    <input id="media-upload" ref="fileInput" type="file" class="d-none" :accept="computedAcceptTypes"
      :disabled="disabled" :multiple="multiple" @change="handleFileChange">

    <!-- Preview Modal -->
    <div class="overlay" :class="{ 'overlayshow': modal.preview }"></div>
    <div class="popup-wrapper" :class="{ 'showing': modal.preview }">
      <div class="popup-card p25">
        <div class="d-flex justify-content-between align-items-center">
          <div class="font-bold">
            <a v-if="currentPreviewFile" :href="currentPreviewFile.previewUrl" target="_blank">
              {{ currentPreviewFile.name }}
              <!-- - {{ currentPreviewFile.size }} -->
            </a>
          </div>
          <button type="button" @click="closePreview" class="btn btn-for-close">
            <span class="icon-ico-close"></span>
          </button>
        </div>
        <div class="inner-land h-auto">
          <div class="popup-item">
            <div class="row gap-2 justify-content-center">
              <!-- Image Preview -->
              <img v-if="currentPreviewFile && currentPreviewFile.type === 'image'" :src="currentPreviewFile.previewUrl"
                alt="preview" class="mt-2 preview-image"
                style="width: 100%; max-width: 300px; object-fit: contain; border: 1px solid #e9e9e9; border-radius: 4px;" />

              <!-- PDF Preview -->
              <div v-if="currentPreviewFile && currentPreviewFile.type === 'pdf'" class="mt-2 pdf-preview">
                <iframe :src="currentPreviewFile.previewUrl" class="pdf-frame" frameborder="0" @load="onPdfLoad"
                  @error="onPdfError"></iframe>
                <div v-if="pdfError" class="pdf-error">
                  <p>Cannot preview PDF in browser. File uploaded successfully.</p>
                  <a :href="currentPreviewFile.previewUrl" target="_blank" class="pdf-download">
                    📄 Open PDF in new tab
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <small class="text-danger text-sm" v-show="errors.has(name)">
      {{ errors.first(name) }}
    </small>
  </div>
</template>

<script>
import AttachIcon from '@/assets/icons/attach.svg'
import ImgPlusIcon from '@/assets/icons/image-plus.svg'
import { truncate } from '@/utils'

export default {
  name: 'FieldMedia',
  props: {
    name: { type: String, required: true },
    label: { type: String, default: null },
    value: { type: [File, Array, String, null], default: null },
    rules: { type: String, default: '' },
    type: {
      type: String,
      default: 'image',
      validator: value => ['image', 'pdf', 'image,pdf', 'pdf,image'].includes(value)
    },
    acceptTypes: { type: String, default: null },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    maxFiles: { type: Number, default: 10 },
    maxFileSize: { type: Number, default: 10 * 1024 * 1024 }, // 10MB default
  },
  data() {
    return {
      truncate,
      files: [],
      currentPreviewFile: null,
      pdfError: false,
      hasInteracted: false, // Track if user has interacted with the field
      modal: {
        preview: false
      }
    };
  },
  computed: {
    currentValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    computedAcceptTypes() {
      if (this.acceptTypes) {
        return this.acceptTypes;
      }

      const typeMap = {
        'image': '.jpg, .jpeg, .png, .gif, .webp',
        'pdf': '.pdf',
        'image,pdf': '.jpg, .jpeg, .png, .gif, .webp, .pdf',
        'pdf,image': '.jpg, .jpeg, .png, .gif, .webp, .pdf'
      };

      return typeMap[this.type] || typeMap['image'];
    },

    allowedFileTypes() {
      const types = this.type.split(',').map(t => t.trim());
      const typeMap = {
        'image': ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'],
        'pdf': ['application/pdf']
      };

      let allowedTypes = [];
      types.forEach(type => {
        if (typeMap[type]) {
          allowedTypes = allowedTypes.concat(typeMap[type]);
        }
      });

      return allowedTypes;
    },

    fieldIcon() {
      return this.type === 'image' ? ImgPlusIcon : AttachIcon
    },

    displayText() {
      if (this.files.length === 0) {
        return this.placeholder || (this.multiple ? 'Choose files...' : 'Choose file...');
      }

      if (this.multiple) {
        return `${this.files.length} file${this.files.length > 1 ? 's' : ''} selected`;
      } else {
        return this.files[0].name;
      }
    },

    // Validation value for the hidden input
    validationValue() {
      if (this.multiple) {
        return this.files.length > 0 ? 'has-files' : '';
      } else {
        return this.files.length > 0 ? 'has-file' : '';
      }
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.updatePreview(newValue);
      }
    },

    // Watch validationValue to trigger validation updates only when there's an actual change
    validationValue(newVal, oldVal) {
      // Only validate if the value actually changed and it's not the initial load
      if (newVal !== oldVal && this.hasInteracted) {
        this.$nextTick(() => {
          if (this.$refs.validationInput) {
            this.$validator.validate(this.name, newVal);
          }
        });
      }
    }
  },

  methods: {
    handleFileChange(e) {
      this.hasInteracted = true; // Mark as interacted when user selects files
      const selectedFiles = Array.from(e.target.files);

      if (selectedFiles.length === 0) {
        if (!this.multiple) {
          this.clearFiles();
        }
        return;
      }

      // Check max files limit
      if (this.multiple && (this.files.length + selectedFiles.length) > this.maxFiles) {
        this.showError(`Maximum ${this.maxFiles} files allowed. You can select ${this.maxFiles - this.files.length} more files.`);
        return;
      }

      const validFiles = [];
      const invalidFiles = [];

      selectedFiles.forEach(file => {
        if (!this.isAcceptedType(file)) {
          invalidFiles.push(file.name);
        } else if (file.size > this.maxFileSize) {
          invalidFiles.push(`${file.name} (file too large)`);
        } else {
          validFiles.push(file);
        }
      });

      if (invalidFiles.length > 0) {
        const allowedTypesText = this.getAllowedTypesText();
        const maxSizeText = this.formatFileSize(this.maxFileSize);
        this.showError(`Some files are invalid: ${invalidFiles.join(', ')}. Please choose valid ${allowedTypesText} files under ${maxSizeText}.`);
      }

      if (validFiles.length > 0) {
        if (this.multiple) {
          this.addFiles(validFiles);
        } else {
          this.setFile(validFiles[0]);
        }
      }

      this.clearInputValue();
    },

    setFile(file) {
      const fileObj = this.createFileObject(file);
      this.files = [fileObj];
      this.currentValue = this.multiple ? [file] : file;
    },

    addFiles(files) {
      const fileObjects = files.map(file => this.createFileObject(file));
      this.files.push(...fileObjects);

      const allFiles = this.files.map(f =>
        f.previewUrl?.startsWith('blob:') ? f.originalFile : f.previewUrl
      );
      this.currentValue = this.multiple ? allFiles : allFiles[0];
    },

    createFileObject(file) {
      return {
        originalFile: file,
        name: file.name,
        size: this.formatFileSize(file.size),
        type: this.getFileType(file),
        previewUrl: URL.createObjectURL(file)
      };
    },

    removeFile(index) {
      this.hasInteracted = true; // Mark as interacted when user removes files
      // Revoke object URL to prevent memory leaks
      if (this.files[index].previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(this.files[index].previewUrl);
      }

      this.files.splice(index, 1);

      if (this.files.length === 0) {
        this.currentValue = this.multiple ? [] : null;
      } else {
        const allFiles = this.files.map(f =>
          f.previewUrl?.startsWith('blob:') ? f.originalFile : f.previewUrl
        );
        this.currentValue = this.multiple ? allFiles : allFiles[0];
      }
    },

    clearFiles() {
      // Revoke all object URLs
      this.files.forEach(file => {
        if (file.previewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });

      this.files = [];
      this.currentValue = this.multiple ? [] : null;
      this.clearInputValue();
    },

    clearInputValue() {
      this.$nextTick(() => {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      });
    },

    // Keep this method for manual validation when needed (like form submission)
    validateField() {
      this.$nextTick(() => {
        if (this.$refs.validationInput) {
          this.$validator.validate(this.name, this.validationValue);
        }
      });
    },

    isAcceptedType(file) {
      return this.allowedFileTypes.includes(file.type);
    },

    getFileType(file) {
      if (file.type.startsWith('image/')) {
        return 'image';
      } else if (file.type === 'application/pdf') {
        return 'pdf';
      }
      return 'unknown';
    },

    getAllowedTypesText() {
      const types = this.type.split(',').map(t => t.trim());
      if (types.length === 1) {
        return types[0];
      } else {
        return types.join(' or ');
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    updatePreview(value) {
      if (!value) {
        this.clearFiles();
      } else if (Array.isArray(value)) {
        // Handle array of files
        this.files = value.map(file => {
          if (file instanceof File) {
            return this.createFileObject(file);
          } else if (typeof file === 'string') {
            return this.createFileObjectFromUrl(file);
          }
        }).filter(Boolean);
      } else if (value instanceof File) {
        this.files = [this.createFileObject(value)];
      } else if (typeof value === 'string') {
        this.files = [this.createFileObjectFromUrl(value)];
      }

      // Don't trigger validation here - let the watcher handle it
      // when user actually interacts with the field
    },

    createFileObjectFromUrl(url) {
      return {
        originalFile: null,
        name: this.extractFileName(url),
        size: 'Unknown',
        type: this.getFileTypeFromUrl(url),
        previewUrl: url
      };
    },

    getFileTypeFromUrl(url) {
      const extension = url.split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
        return 'image';
      } else if (extension === 'pdf') {
        return 'pdf';
      }
      return 'unknown';
    },

    extractFileName(url) {
      try {
        return url.split('/').pop().split('?')[0] || 'Existing file';
      } catch {
        return 'Existing file';
      }
    },

    openPreview(file) {
      this.currentPreviewFile = file;
      this.modal.preview = true;
      this.pdfError = false;
    },

    closePreview() {
      this.modal.preview = false;
      this.currentPreviewFile = null;
      this.pdfError = false;
    },

    showError(message) {
      alert(message); // Replace with toast or global error handler if needed
    },

    onPdfLoad() {
      this.pdfError = false;
    },

    onPdfError() {
      this.pdfError = true;
    }
  },

  // Remove mounted hook - no immediate validation needed
  // mounted() {
  //   this.validateField();
  // },

  beforeDestroy() {
    // Clean up all object URLs
    this.files.forEach(file => {
      if (file.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });
  },

  inject: ['$validator']
};
</script>

<style scoped>
label.media-upload {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #E9E9E9;
  border-radius: 4px;
  padding-left: 15px;
  height: 46.6px;
  cursor: pointer;
}

label.media-upload span {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 16px;
}

label.media-upload .icon {
  width: 46px;
  height: 100%;
  background: #E9E9E9;
  display: grid;
  place-items: center;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

label.media-upload .icon:hover {
  background: #dcdcdc;
  transition: 0.2s all ease-in-out;
}

.file-list {
  max-height: 300px;
  overflow-y: auto;
}

.file-item {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  transition: all 0.2s ease;
  width: 100%;
}

.file-item:hover {
  background: #e9ecef;
  border-color: #dee2e6;
}

.file-type-icon {
  font-size: 16px;
  width: 24px;
  text-align: center;
}

.file-details {
  flex: 1;
  min-width: 0;
}

.file-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
}

.file-size {
  font-size: 12px;
  color: #6c757d;
}

.file-actions {
  flex-shrink: 0;
}

.preview-image {
  max-height: 200px;
}

.pdf-preview {
  border: 1px solid #e9e9e9;
  border-radius: 4px;
  overflow: hidden;
  background: #f9f9f9;
}

.pdf-frame {
  width: 100%;
  height: 300px;
  border: none;
  background: white;
}

.pdf-error {
  padding: 20px;
  text-align: center;
  color: #666;
}

.pdf-error p {
  margin-bottom: 10px;
}

.pdf-download {
  display: inline-block;
  padding: 8px 16px;
  background: #007bff;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 14px;
}

.pdf-download:hover {
  background: #0056b3;
  color: white;
  text-decoration: none;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.overlay.overlayshow {
  opacity: 1;
  visibility: visible;
}

.popup-wrapper {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  z-index: 1001;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  max-width: 90vw;
  max-height: 90vh;
}

.popup-wrapper.showing {
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, -50%) scale(1);
}

.popup-card {
  background: white;
  border-radius: 8px;
  max-width: 500px;
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}
</style>
